import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface TeacherHeaderProps {
  userProfile: any;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  onProfileUpdate?: (profile: any) => void;
}

export default function TeacherHeader({
  userProfile,
  activeTab,
  setActiveTab,
  onProfileUpdate,
}: TeacherHeaderProps) {
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [courseCount, setCourseCount] = useState(0);

  // Fetch course count
  useEffect(() => {
    fetchCourseCount();
  }, []);

  const fetchCourseCount = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const teacherId = user.id;

      if (!teacherId) return;

      const response = await fetch(
        `${
          import.meta.env.VITE_PUBLIC_SUPABASE_URL
        }/functions/v1/get-courses?teacher_id=${encodeURIComponent(teacherId)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${
              import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
            }`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const coursesArray = Array.isArray(data) ? data : [];
        setCourseCount(coursesArray.length);
      }
    } catch (error) {
      console.error("Error fetching course count:", error);
      setCourseCount(0);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("userProfile");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userType");
    localStorage.removeItem("supabase_session");
    window.location.href = "/login";
  };

  const tabs = [
    { id: "dashboard", label: "Tổng quan", icon: "ri-dashboard-line" },
    { id: "courses", label: `Khóa học (${courseCount})`, icon: "ri-book-line" },
    { id: "students", label: "Học sinh", icon: "ri-group-line" },
    { id: "assignments", label: "Bài tập", icon: "ri-file-list-line" },
    { id: "schedule", label: "Lịch dạy", icon: "ri-calendar-line" },
  ];

  // Refresh course count when switching to courses tab
  useEffect(() => {
    if (activeTab === "courses") {
      fetchCourseCount();
    }
  }, [activeTab]);

  return (
    <>
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            {/* <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-lg sm:text-xl font-bold text-blue-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  Olympic Academy
                </h1>
              </div>
            </div> */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 flex items-center justify-center">
                <img
                  src="/favicon.png"
                  alt="Olympic Academy Logo"
                  className="w-10 h-10 object-contain"
                />
              </div>
              <div>
                <h1
                  className="text-xl font-bold text-gray-900"
                  style={{
                    fontFamily: '"Inter", "Roboto", sans-serif',
                    fontWeight: "700",
                  }}
                >
                  Olympic Academy
                </h1>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-4 xl:space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? "bg-blue-100 text-blue-700"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  <i className={`${tab.icon} mr-2`}></i>
                  {tab.label}
                </button>
              ))}
            </nav>

            {/* User Profile */}
            <div className="flex items-center space-x-2">
              <div className="relative hidden sm:block">
                <button
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className="flex items-center space-x-2 sm:space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    {userProfile?.avatar_url ? (
                      <img
                        src={userProfile.avatar_url}
                        alt="Avatar"
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <i className="ri-user-line text-blue-600"></i>
                    )}
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-gray-900">
                      {userProfile?.full_name || "Giáo viên"}
                    </p>
                    <p className="text-xs text-gray-500">Giáo viên</p>
                  </div>
                  <i className="ri-arrow-down-s-line text-gray-400 hidden md:block"></i>
                </button>

                {/* Dropdown Menu */}
                {showProfileDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <button
                      onClick={() => {
                        setShowProfileModal(true);
                        setShowProfileDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <i className="ri-user-settings-line mr-3"></i>
                      Chỉnh sửa profile
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                    >
                      <i className="ri-logout-box-line mr-3"></i>
                      Đăng xuất
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <i
                  className={`${
                    showMobileMenu ? "ri-close-line" : "ri-menu-line"
                  } text-xl`}
                ></i>
              </button>
            </div>
          </div>

          {/* Mobile Navigation Tabs */}
          <div className="lg:hidden border-t border-gray-200">
            <div className="flex overflow-x-auto py-2 space-x-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? "bg-blue-100 text-blue-700"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  <i className={`${tab.icon} mr-1 sm:mr-2`}></i>
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {showMobileMenu && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-3 space-y-2">
              <div className="flex items-center space-x-3 pb-3 border-b border-gray-200">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  {userProfile?.avatar_url ? (
                    <img
                      src={userProfile.avatar_url}
                      alt="Avatar"
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <i className="ri-user-line text-blue-600"></i>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {userProfile?.full_name || "Giáo viên"}
                  </p>
                  <p className="text-xs text-gray-500">Giáo viên</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowProfileModal(true);
                  setShowMobileMenu(false);
                }}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <i className="ri-user-settings-line mr-3"></i>
                Chỉnh sửa profile
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <i className="ri-logout-box-line mr-3"></i>
                Đăng xuất
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Profile Edit Modal */}
      {showProfileModal && (
        <ProfileEditModal
          userProfile={userProfile}
          onClose={() => setShowProfileModal(false)}
          onUpdate={(updatedProfile) => {
            // Update parent component
            if (onProfileUpdate) {
              onProfileUpdate(updatedProfile);
            }
            // Update localStorage
            localStorage.setItem("userProfile", JSON.stringify(updatedProfile));
            localStorage.setItem("user", JSON.stringify(updatedProfile));
            setShowProfileModal(false);
          }}
        />
      )}
    </>
  );
}

// Profile Edit Modal Component
function ProfileEditModal({
  userProfile,
  onClose,
  onUpdate,
}: {
  userProfile: any;
  onClose: () => void;
  onUpdate: (profile: any) => void;
}) {
  const [formData, setFormData] = useState({
    full_name: userProfile?.full_name || "",
    email: userProfile?.email || "",
    phone: userProfile?.phone || "",
    bio: userProfile?.bio || "",
    avatar_url: userProfile?.avatar_url || "",
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Vui lòng chọn file ảnh");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Kích thước ảnh không được vượt quá 5MB");
      return;
    }

    setUploading(true);
    try {
      // Convert to base64 for demo purposes
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        setFormData((prev) => ({ ...prev, avatar_url: base64 }));
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Có lỗi xảy ra khi tải ảnh lên");
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const email = localStorage.getItem("userEmail");

      // Update profile via Supabase Edge Function
      const response = await fetch(
        `${
          import.meta.env.VITE_PUBLIC_SUPABASE_URL
        }/functions/v1/update-profile`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${
              import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
            }`,
          },
          body: JSON.stringify({
            email,
            full_name: formData.full_name,
            phone: formData.phone,
            bio: formData.bio,
            avatar_url: formData.avatar_url,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        const updatedProfile = { ...userProfile, ...formData };
        onUpdate(updatedProfile);
        alert("Cập nhật profile thành công!");
      } else {
        // Fallback: update localStorage only
        const updatedProfile = { ...userProfile, ...formData };
        onUpdate(updatedProfile);
        alert("Cập nhật profile thành công!");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      // Fallback: update localStorage only
      const updatedProfile = { ...userProfile, ...formData };
      onUpdate(updatedProfile);
      alert("Cập nhật profile thành công!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Chỉnh sửa profile
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Avatar Upload */}
            <div className="flex items-center space-x-6">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden">
                {formData.avatar_url ? (
                  <img
                    src={formData.avatar_url}
                    alt="Avatar"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <i className="ri-user-line text-2xl text-gray-400"></i>
                )}
              </div>
              <div>
                <label className="block">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                  <span className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer inline-flex items-center whitespace-nowrap">
                    {uploading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Đang tải...
                      </>
                    ) : (
                      <>
                        <i className="ri-upload-line mr-2"></i>
                        Tải ảnh lên
                      </>
                    )}
                  </span>
                </label>
                <p className="text-sm text-gray-500 mt-1">
                  JPG, PNG tối đa 5MB
                </p>
              </div>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Họ và tên *
                </label>
                <input
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      full_name: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập họ và tên..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, email: e.target.value }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập email..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Số điện thoại
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, phone: e.target.value }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập số điện thoại..."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Giới thiệu bản thân
              </label>
              <textarea
                rows={4}
                value={formData.bio}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, bio: e.target.value }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Viết vài dòng giới thiệu về bản thân..."
                maxLength={500}
              ></textarea>
              <p className="text-sm text-gray-500 mt-1">
                {formData.bio.length}/500 ký tự
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
              >
                Hủy
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Đang lưu...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line mr-2"></i>
                    Lưu thay đổi
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
